/**
 * API Service
 * Centralized HTTP client with type safety and error handling
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '../config/app.config'
import { ApiErrorResponse, StrapiResponse } from '../types/api'
import { getJwtToken } from '../utils/localStorage'
import { isJwtExpired, logoutAndRedirectToLogin } from '../utils/authSession'

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
	constructor(
		message: string,
		public status: number,
		public code?: string,
		public details?: unknown,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

/**
 * API Service Class
 * Provides typed HTTP methods with automatic auth token injection
 */
class ApiService {
	private client: AxiosInstance

	constructor() {
		this.client = axios.create({
			baseURL: API_BASE_URL,
			timeout: 10000,
			headers: {
				'Content-Type': 'application/json',
			},
		})

		this.setupInterceptors()
	}

	/**
	 * Setup request and response interceptors
	 */
	private setupInterceptors(): void {
		// Request interceptor - add auth token
		this.client.interceptors.request.use(
			(config) => {
				const token = getJwtToken()
				if (token && isJwtExpired(token)) {
					logoutAndRedirectToLogin()
					return Promise.reject(new Error('Session expired'))
				}
				if (token && config.headers) {
					config.headers.Authorization = `Bearer ${token}`
				}
				return config
			},
			(error) => Promise.reject(error),
		)

		// Response interceptor - handle errors
		this.client.interceptors.response.use(
			(response) => response,
			(error: AxiosError<ApiErrorResponse>) => {
				if (error.response) {
					const { status } = error.response

					// Handle 401 Unauthorized (e.g. revoked or invalid token on server)
					if (status === 401) {
						logoutAndRedirectToLogin()
					}
				}

				return Promise.reject(this.handleError(error))
			},
		)
	}

	/**
	 * Handle and transform axios errors
	 */
	private handleError(error: AxiosError<ApiErrorResponse>): ApiError {
		if (error.response) {
			// Server responded with error
			const { status, data } = error.response
			const message = data?.error?.message || error.message || 'An error occurred'

			return new ApiError(message, status, error.code, data?.error?.details)
		} else if (error.request) {
			// Request made but no response
			return new ApiError('No response from server', 0, error.code || 'ERR_NETWORK')
		} else {
			// Error in request setup
			return new ApiError(error.message || 'Request failed', 0, error.code)
		}
	}

	/**
	 * GET request with type safety
	 */
	async get<T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig): Promise<StrapiResponse<T>> {
		const response = await this.client.get<StrapiResponse<T>>(url, {
			params,
			...config,
		})
		return response.data
	}

	/**
	 * POST request with type safety
	 */
	async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<StrapiResponse<T>> {
		const response = await this.client.post<StrapiResponse<T>>(url, data, config)
		return response.data
	}

	/**
	 * PUT request with type safety
	 */
	async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<StrapiResponse<T>> {
		const response = await this.client.put<StrapiResponse<T>>(url, data, config)
		return response.data
	}

	/**
	 * PATCH request with type safety
	 */
	async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<StrapiResponse<T>> {
		const response = await this.client.patch<StrapiResponse<T>>(url, data, config)
		return response.data
	}

	/**
	 * DELETE request with type safety
	 */
	async delete<T>(url: string, config?: AxiosRequestConfig): Promise<StrapiResponse<T>> {
		const response = await this.client.delete<StrapiResponse<T>>(url, config)
		return response.data
	}

	/**
	 * Get the underlying axios instance for advanced usage
	 */
	getClient(): AxiosInstance {
		return this.client
	}
}

// Export singleton instance
const apiService = new ApiService()
export default apiService
